import { FC, useRef, useState } from 'react'
import styled from 'styled-components'
import { FileUploader } from 'react-drag-drop-files'
import { Icon, IconColor, IconSize, IconTypes } from './Icon'
import { useTranslation } from 'react-i18next'

const FILE_TYPES = [ 'JPEG', 'JPG', 'PNG', 'SVG', 'WEBP', 'GIF' ]
const MAX_SIZE_MB = 3

interface ImageDropzoneProps {
  className?: string;
  title?: string;
  subtitle?: string;
  onFileChange: (file: File) => void;
  onError?: (msg: string) => void;
  initialUrl?: string;
  fileTypes?: string[];
  maxSizeMb?: number;
}

const ImageDropzone: FC<ImageDropzoneProps> = ({
  className,
  title = 'Drop file here or',
  subtitle = 'Click here to upload',
  onFileChange,
  onError,
  initialUrl,
  fileTypes = FILE_TYPES,
  maxSizeMb = MAX_SIZE_MB,
}) => {
  const { t } = useTranslation()
  const fileUploaderRef = useRef<HTMLDivElement>(null)
  const [ file, setFile ] = useState<File | null>(null)

  const handleChange = (incoming: File) => {
    const ext = incoming.type.split('/')[1]?.toLocaleUpperCase()
    if (!fileTypes.includes(ext)) {
      onError?.('Invalid file format')
      return
    }
    if (incoming.size / 1_000_000 > maxSizeMb) {
      onError?.(`File is too large (max ${maxSizeMb}MB)`)
      return
    }
    setFile(incoming)
    onFileChange(incoming)
  }

  const handleRemove = () => {
    setFile(null)
    onFileChange(null)
  }

  return (
    <Wrapper className={className}>
      <div ref={fileUploaderRef} style={{ width: '100%' }}>
        <DropArea className="image-dropzone-area">
          <FileUploader
            handleChange={handleChange}
            name="file"
            types={fileTypes}
          >
            <DropContent className="image-dropzone-content">
              <IconBox className="image-dropzone-icon">
                <Icon
                  type={IconTypes.upload}
                  color={IconColor.viloet400}
                  height={10}
                  width={10}
                  size={IconSize.small}
                />
              </IconBox>
              <DropTitle>{title}</DropTitle>
              <UploadButton type="button">
                <DropSubtitle>{subtitle}</DropSubtitle>
              </UploadButton>
            </DropContent>
          </FileUploader>
        </DropArea>

        {(file || initialUrl) && (
          <FileCard className="image-dropzone-file-card">
            {file && (
              <RemoveButton type="button" onClick={handleRemove}>
                <Icon
                  type={IconTypes.close}
                  color={IconColor.white}
                  height={16}
                  width={16}
                />
              </RemoveButton>
            )}
            <Thumbnail>
              <ThumbnailImage src={file ? URL.createObjectURL(file) : initialUrl} alt={t('channels.preview')} />
            </Thumbnail>
            <FileDetails>
              <FileName>{file ? file.name : t('ui.currentCover')}</FileName>
              {file && <FileSize>{t('login.valueKb', { value: (file.size / 1024).toFixed(0) })}</FileSize>}
              <ProgressRow>
                <ProgressBar>
                  <ProgressFill />
                </ProgressBar>
                <span>100%</span>
              </ProgressRow>
            </FileDetails>
          </FileCard>
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
`

const DropArea = styled.div`
  display: flex;
  padding: 16px;
  width: 100%;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-input-border-primary);
  outline: none;
  margin-bottom: 8px;
  border-radius: 10px;
  background-color: var(--container-background);
`

const DropContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 340px;
  outline: none;
`

const IconBox = styled.div`
  padding: 10px;
  background: var(--icon-container-background);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border: 1px solid var(--color-input-border-primary);
`

const DropTitle = styled.p`
  color: var(--text-color-secondary);
  font-size: var(--font-size-large);
  font-weight: 600;
  padding-bottom: 5px;
  margin: 0;
`

const UploadButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  outline: none;
`

const DropSubtitle = styled.p`
  color: var(--text-color-tertiary);
  font-weight: 400;
  font: inherit;
  margin: 0;
`

const FileCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: var(--container-background);
  border: 1px solid var(--color-input-border-primary);
  border-radius: 12px;
  width: 100%;
`

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  border: none;
  border-radius: 50%;
  background: transparent;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
`

const Thumbnail = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: rgba(123, 63, 242, 0.1);
  flex-shrink: 0;
  overflow: hidden;
`

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const FileDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const FileName = styled.p`
  color: var(--text-color);
  font-size: var(--font-size-normal);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`

const FileSize = styled.p`
  color: var(--color-input-label-primary);
  font-size: var(--font-size-small);
  margin: 0;
`

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
  color: var(--text-color-secondary);
  font-size: var(--font-size-small);
  font-weight: 500;
`

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: rgba(123, 63, 242, 0.2);
  border-radius: 2px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  width: 100%;
  height: 100%;
  background: var(--button-primary-color);
  border-radius: 2px;
`

export default ImageDropzone
